import { api } from '../client';
import type { PageParams } from '@/types/api';
import type { Permission, Role, User, UUID } from '@/types/domain';

export interface CreateUserPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  roleIds: UUID[];
}

export interface UpdateUserPayload {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  status: User['status'];
  roleIds: UUID[];
}

export interface RolePayload {
  code: string;
  name: string;
  description?: string;
  permissionIds: UUID[];
}

export const usersApi = {
  list: (params: PageParams & { status?: User['status'] }) => api.getPage<User>('/users', { params }),
  get: (id: UUID) => api.get<User>(`/users/${id}`),
  create: (payload: CreateUserPayload) => api.post<User>('/users', payload),
  update: (id: UUID, payload: UpdateUserPayload) => api.put<User>(`/users/${id}`, payload),
  deactivate: (id: UUID) => api.delete(`/users/${id}`),
  /**
   * Hands the account a temporary password the owner must replace at next
   * sign-in. Strength is checked by the server, which says why it refused.
   */
  resetPassword: (id: UUID, temporaryPassword: string) =>
    api.post<User>(`/users/${id}/reset-password`, { temporaryPassword }),
};

/** Roles and permissions are small, fixed sets returned unpaginated by design. */
export const rolesApi = {
  list: () => api.get<Role[]>('/roles'),
  get: (id: UUID) => api.get<Role>(`/roles/${id}`),
  create: (payload: RolePayload) => api.post<Role>('/roles', payload),
  update: (id: UUID, payload: RolePayload) => api.put<Role>(`/roles/${id}`, payload),
  remove: (id: UUID) => api.delete(`/roles/${id}`),
};

export const permissionsApi = {
  list: () => api.get<Permission[]>('/permissions'),
};
